/**
 * ============================================
 * logger - 개발 환경용 로깅 유틸리티
 * ============================================
 *
 * 【설명】
 * 개발 환경에서만 동작하는 로깅 유틸리티입니다.
 * 프로덕션 환경에서는 자동으로 로그가 출력되지 않아 성능과 보안을 보장합니다.
 * 로그 레벨을 설정하여 필요한 수준의 로그만 출력할 수 있습니다.
 *
 * 【사용법】
 * import { logger } from './logger';
 *
 * // 개발 중 디버깅
 * logger.debug('변수 값:', someVariable);
 * logger.log('일반 로그');
 * logger.info('✓ 작업 완료');
 * logger.warn('⚠️ 경고 메시지');
 * logger.error('❌ 에러 발생:', error);
 *
 * 【로그 레벨】
 * 5가지 레벨을 제공하며, 설정된 레벨 이상만 출력됩니다:
 *
 * 1. debug (가장 상세)
 *    - 개발 중 변수 값, 함수 호출 흐름 등 상세 정보
 *    - 예: logger.debug('API 요청 파라미터:', params);
 *
 * 2. log (일반)
 *    - 일반적인 정보성 로그
 *    - 예: logger.log('컴포넌트 마운트됨');
 *
 * 3. info (정보)
 *    - 중요한 정보나 완료 메시지
 *    - 예: logger.info('✓ 데이터 로드 완료');
 *
 * 4. warn (경고)
 *    - 경고 메시지, 잠재적 문제
 *    - 예: logger.warn('⚠️ API 응답 느림');
 *
 * 5. error (에러, 가장 중요)
 *    - 에러 메시지, 예외 상황
 *    - 예: logger.error('❌ 네트워크 오류:', error);
 *
 * 【환경별 동작】
 * - 개발 환경 (NODE_ENV !== 'production'):
 *   모든 로그가 console에 출력됩니다.
 *
 * - 프로덕션 환경 (NODE_ENV === 'production'):
 *   모든 로그가 무시됩니다 (빈 함수로 동작).
 *   성능 오버헤드 없음, 보안 정보 노출 방지.
 *
 * 【로그 레벨 설정】
 * LOG_LEVEL 상수를 변경하여 출력할 최소 레벨을 설정할 수 있습니다:
 *
 * const LOG_LEVEL = 'debug';  // 모든 로그 출력 (기본값)
 * const LOG_LEVEL = 'info';   // info, warn, error만 출력
 * const LOG_LEVEL = 'warn';   // warn, error만 출력
 * const LOG_LEVEL = 'error';  // error만 출력
 *
 * 현재 설정: 'info' (debug, log은 출력 안 됨)
 *
 * 【예제 1: 기본 사용】
 * function fetchData() {
 *   logger.info('데이터 요청 시작');
 *
 *   fetch('/api/data')
 *     .then(response => {
 *       logger.debug('응답 상태:', response.status);
 *       return response.json();
 *     })
 *     .then(data => {
 *       logger.info('✓ 데이터 로드 완료:', data.length, '개');
 *     })
 *     .catch(error => {
 *       logger.error('❌ 데이터 로드 실패:', error);
 *     });
 * }
 *
 * 【예제 2: API 호출 로깅】
 * async function sendMessage(message) {
 *   logger.debug('API 호출 파라미터:', { message });
 *
 *   try {
 *     const response = await fetch('/api/chat', {
 *       method: 'POST',
 *       body: JSON.stringify({ message })
 *     });
 *
 *     if (!response.ok) {
 *       logger.warn('⚠️ API 응답 코드:', response.status);
 *     }
 *
 *     const data = await response.json();
 *     logger.info('✓ 메시지 전송 완료');
 *     return data;
 *   } catch (error) {
 *     logger.error('❌ 메시지 전송 실패:', error);
 *     throw error;
 *   }
 * }
 *
 * 【예제 3: 컴포넌트 라이프사이클】
 * function MyComponent() {
 *   useEffect(() => {
 *     logger.info('✓ MyComponent 마운트됨');
 *
 *     return () => {
 *       logger.info('MyComponent 언마운트됨');
 *     };
 *   }, []);
 *
 *   const handleClick = () => {
 *     logger.debug('버튼 클릭됨');
 *   };
 *
 *   return <button onClick={handleClick}>클릭</button>;
 * }
 *
 * 【예제 4: 에러 핸들링】
 * function parseData(jsonString) {
 *   try {
 *     const data = JSON.parse(jsonString);
 *     logger.debug('파싱 성공:', data);
 *     return data;
 *   } catch (error) {
 *     logger.error('❌ JSON 파싱 실패:', error.message);
 *     logger.debug('원본 문자열:', jsonString);
 *     return null;
 *   }
 * }
 *
 * 【의존성】
 * - process.env.NODE_ENV (Node.js 환경 변수)
 *   Create React App에서는 자동으로 설정됩니다:
 *   - npm start → development
 *   - npm run build → production
 *
 * - 외부 라이브러리: 없음
 *   순수 JavaScript로 작동
 *
 * 【재사용 가능】
 * ✅ 이 파일은 완전히 독립적이며 다른 프로젝트에서도 사용 가능합니다.
 * ✅ JavaScript/TypeScript 프로젝트에 복사하여 바로 사용하세요.
 * ✅ React, Vue, Angular 등 모든 프레임워크에서 사용 가능합니다.
 * ✅ Node.js 백엔드 코드에서도 사용 가능합니다.
 *
 * 【커스터마이징】
 * 1. 로그 레벨 변경:
 *    LOG_LEVEL 상수 값을 원하는 레벨로 변경
 *    ('debug', 'log', 'info', 'warn', 'error')
 *
 * 2. 이모지 추가/제거:
 *    각 logger 메서드에서 이모지 제거하거나 변경 가능
 *
 * 3. 로그 포맷 변경:
 *    타임스탬프, 파일명, 라인 번호 등 추가 가능
 *    예: logger.info(`[${new Date().toISOString()}]`, message);
 *
 * 4. 원격 로깅 추가:
 *    프로덕션에서도 에러를 서버로 전송하도록 확장 가능
 *    예: Sentry, LogRocket 등과 통합
 *
 * 5. 파일 로깅 (Node.js):
 *    console 대신 fs.appendFile로 파일에 기록 가능
 *
 * 【주의사항】
 * ⚠️ 민감한 정보 로깅 금지:
 *    비밀번호, API 키, 개인정보 등은 로그에 남기지 마세요.
 *    개발 환경에서도 보안에 주의해야 합니다.
 *
 * ⚠️ 과도한 로깅 주의:
 *    loop 안에서 logger를 호출하면 성능 저하 발생 가능.
 *    필요한 경우에만 사용하세요.
 *
 * ⚠️ 프로덕션 빌드 확인:
 *    배포 전 NODE_ENV=production으로 빌드되는지 확인하세요.
 *    로그가 출력되지 않아야 정상입니다.
 */

type LogLevel = 'debug' | 'log' | 'info' | 'warn' | 'error';

// 개발 환경 체크
const isDevelopment: boolean = process.env.NODE_ENV !== 'production';

/**
 * 로그 레벨 설정
 * 'debug' < 'log' < 'info' < 'warn' < 'error'
 * 설정된 레벨 이상만 출력됩니다.
 */
const LOG_LEVEL: LogLevel = 'info';

// 로그 레벨 우선순위
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  log: 1,
  info: 2,
  warn: 3,
  error: 4,
};

const currentLogLevel: number = LOG_LEVELS[LOG_LEVEL] ?? 0;

/**
 * 로그를 출력할지 확인
 * @param level - 로그 레벨
 */
const shouldLog = (level: LogLevel): boolean => {
  return isDevelopment && LOG_LEVELS[level] >= currentLogLevel;
};

/**
 * 로깅 유틸리티 객체
 */
export const logger = {
  /**
   * 디버그 로그 (가장 상세한 정보)
   */
  debug: (...args: unknown[]): void => {
    if (shouldLog('debug')) {
      console.log('🔍', ...args);
    }
  },

  /**
   * 일반 로그
   */
  log: (...args: unknown[]): void => {
    if (shouldLog('log')) {
      console.log(...args);
    }
  },

  /**
   * 정보 로그 (중요한 정보)
   */
  info: (...args: unknown[]): void => {
    if (shouldLog('info')) {
      console.log('ℹ️', ...args);
    }
  },

  /**
   * 경고 로그
   */
  warn: (...args: unknown[]): void => {
    if (shouldLog('warn')) {
      console.warn('⚠️', ...args);
    }
  },

  /**
   * 에러 로그 (가장 중요)
   */
  error: (...args: unknown[]): void => {
    if (shouldLog('error')) {
      console.error('❌', ...args);
    }
  },
};
